import { useState } from 'react'

export interface PollDraft {
  question: string
  options: string[]
  isAnonymous: boolean
  allowRevote: boolean
  expiresAt: string | null
}

interface Props {
  value: PollDraft | null
  onChange: (poll: PollDraft | null) => void
}

const EMPTY: PollDraft = {
  question: '',
  options: ['', ''],
  isAnonymous: false,
  allowRevote: true,
  expiresAt: null,
}

export default function PollBuilder({ value, onChange }: Props) {
  const [showExpiry, setShowExpiry] = useState(false)
  const isActive = value !== null
  const poll = value ?? EMPTY

  const toggle = () => onChange(isActive ? null : { ...EMPTY })
  const update = (patch: Partial<PollDraft>) => onChange({ ...poll, ...patch })

  const setOption = (i: number, text: string) => {
    const next = [...poll.options]
    next[i] = text
    update({ options: next })
  }

  const addOption = () => {
    if (poll.options.length >= 6) return
    update({ options: [...poll.options, ''] })
  }

  const removeOption = (i: number) => {
    if (poll.options.length <= 2) return
    update({ options: poll.options.filter((_, idx) => idx !== i) })
  }

  const toggleExpiry = () => {
    if (showExpiry) { setShowExpiry(false); update({ expiresAt: null }) }
    else setShowExpiry(true)
  }

  // datetime-local inputs read/write local time (no timezone info), while we
  // store expiresAt as a UTC ISO string — convert using the local offset so the
  // picker doesn't drift from what the user actually selected.
  const toLocalInputValue = (iso: string) => {
    const d = new Date(iso)
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
  }

  // Tomorrow as default min date for expiry picker
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const minDate = toLocalInputValue(tomorrow.toISOString())

  return (
    <div style={{
      border: `1px solid ${isActive ? 'rgba(124,58,237,0.4)' : 'var(--border)'}`,
      borderRadius: '10px', overflow: 'hidden',
      background: isActive ? 'rgba(124,58,237,0.04)' : 'transparent',
      transition: 'all 0.15s',
    }}>
      {/* Toggle header */}
      <button
        type="button"
        onClick={toggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem',
          padding: '0.75rem 1rem', background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{
          width: '18px', height: '18px', borderRadius: '4px', border: '2px solid',
          borderColor: isActive ? 'var(--accent-primary)' : 'var(--border)',
          background: isActive ? 'var(--accent-primary)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, fontSize: '11px', color: 'white', transition: 'all 0.15s',
        }}>
          {isActive ? '✓' : ''}
        </span>
        <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>📊 Add a Poll</span>
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginLeft: '0.25rem' }}>
          (optional)
        </span>
      </button>

      {/* Poll form — only when active */}
      {isActive && (
        <div style={{ padding: '0 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>

          {/* Question */}
          <div>
            <label style={labelStyle}>Poll question *</label>
            <input
              value={poll.question}
              onChange={(e) => update({ question: e.target.value })}
              placeholder="e.g. Which framework do you prefer?"
              maxLength={300}
              style={inputStyle}
            />
          </div>

          {/* Options */}
          <div>
            <label style={labelStyle}>Options * <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(min 2, max 6)</span></label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {poll.options.map((opt, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', minWidth: '16px', textAlign: 'center' }}>
                    {i + 1}
                  </span>
                  <input
                    value={opt}
                    onChange={(e) => setOption(i, e.target.value)}
                    placeholder={`Option ${i + 1}`}
                    maxLength={100}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  {poll.options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(i)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: '1rem', padding: '0 0.25rem', lineHeight: 1 }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--error)' }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)' }}
                    >×</button>
                  )}
                </div>
              ))}
              {poll.options.length < 6 && (
                <button
                  type="button"
                  onClick={addOption}
                  style={{
                    padding: '0.35rem 0.75rem', background: 'transparent',
                    border: '1px dashed var(--border)', borderRadius: '6px',
                    color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.78rem',
                    textAlign: 'left', marginLeft: '22px',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = 'var(--accent-primary)'; b.style.color = 'var(--accent-primary)' }}
                  onMouseLeave={(e) => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = 'var(--border)'; b.style.color = 'var(--text-muted)' }}
                >
                  + Add option
                </button>
              )}
            </div>
          </div>

          {/* Settings row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', paddingTop: '0.25rem', borderTop: '1px solid var(--border)' }}>

            {/* Anonymous toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <div
                onClick={() => update({ isAnonymous: !poll.isAnonymous })}
                style={{
                  width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                  background: poll.isAnonymous ? 'var(--accent-primary)' : 'var(--bg-elevated)',
                  outline: '1px solid var(--border)',
                  cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: '2px',
                  left: poll.isAnonymous ? '18px' : '2px',
                  width: '16px', height: '16px', borderRadius: '50%',
                  background: 'white', transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }} />
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Anonymous votes
              </span>
            </label>

            {/* Allow revote toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <div
                onClick={() => update({ allowRevote: !poll.allowRevote })}
                style={{
                  width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                  background: poll.allowRevote ? 'var(--accent-primary)' : 'var(--bg-elevated)',
                  outline: '1px solid var(--border)',
                  cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: '2px',
                  left: poll.allowRevote ? '18px' : '2px',
                  width: '16px', height: '16px', borderRadius: '50%',
                  background: 'white', transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }} />
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Allow changing vote
              </span>
            </label>

            {/* Expiry toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <div
                onClick={toggleExpiry}
                style={{
                  width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                  background: showExpiry ? 'var(--accent-primary)' : 'var(--bg-elevated)',
                  outline: '1px solid var(--border)',
                  cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                }}
              >
                <div style={{
                  position: 'absolute', top: '2px',
                  left: showExpiry ? '18px' : '2px',
                  width: '16px', height: '16px', borderRadius: '50%',
                  background: 'white', transition: 'left 0.2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }} />
              </div>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                Set expiry date
              </span>
            </label>
          </div>

          {/* Expiry date picker */}
          {showExpiry && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Closes at:</span>
              <input
                type="datetime-local"
                min={minDate}
                value={poll.expiresAt ? toLocalInputValue(poll.expiresAt) : ''}
                onChange={(e) => update({ expiresAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                style={{
                  padding: '0.3rem 0.5rem', background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)', borderRadius: '6px',
                  color: 'var(--text-primary)', fontSize: '0.78rem',
                  outline: 'none', fontFamily: 'inherit',
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8rem', fontWeight: 600,
  color: 'var(--text-primary)', marginBottom: '0.4rem',
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.55rem 0.75rem',
  background: 'var(--bg-base)', border: '1px solid var(--border)',
  borderRadius: '6px', color: 'var(--text-primary)',
  fontSize: '0.875rem', outline: 'none',
  fontFamily: 'inherit', boxSizing: 'border-box',
}
